import {
    defaultProps,
    schema,
    views,
    componentName,
    fieldTypes,
    eventMap,
} from "../../../core/components/form/form";
import { componentClasses as textInputClasses } from "../../../core/components/text-input/text-input";
import { FormField } from "../../../core/components/form/form-field";
import { prefix } from "../../../core/design-tokens/meta";
import validateInitialization from "../../../core/js/helpers/validate-initialization";
import { createStore, renderState, dispatchEvent } from "../../../core/js/state-machine";
import { formReducer, actions } from "../../../core/js/state-machine/reducers/form";
import { isEmptyObject, isRequired, randomNumGenerator } from "../../../core/js/utilities";
import { alertNotice } from "../../../core/js/helpers/built-in-icons";
import { componentClasses as checkboxClasses } from "../../../core/components/checkbox/checkbox";

/**
 * Initialize the Form component
 * @param {HTMLElement} element main Form wrapper
 * @param defaultProps
 * @returns {{
 *  element: HTMLElement,
 *  dispose: Function
 *  getErrors: Function
 *  isValid: Function
 * }} public api object
 *
 */
const Form = (element, props = {}) => {
    const [success, mergedProps] = validateInitialization({
        defaultProps,
        componentName,
        element,
        props,
        schema,
    });

    if (!success) return;

    let formFields;
    let hasRunValidation = false;

    /**
     * Capture all the form fields that the form is going to be concerned with.
     * @param {HTMLElement} form
     * @returns {Array} elements of the form.
     */
    const getFormFields = (form) => {
        const _formFields = [];

        Object.keys(fieldTypes).forEach((type) => {
            // Radio buttons are handled uniquely by their fieldset.
            if (["radio", "checkbox", "checkboxGroup"].includes(type)) return;
            // Query by type so that we can preserve the type when creating a formField.
            const fieldElements = Array.from(form.querySelectorAll(`.${fieldTypes[type].field}`));
            fieldElements.forEach((field) => {
                _formFields.push(FormField(field, { type }));
            });
        });

        return _formFields;
    };

    /**
     * Remove all listeners and the public api from the **element**. It makes possible to initialize
     * same element again.
     */
    const dispose = () => {
        element.removeEventListener("submit", handleSubmit);
        element[componentName] = null;
        element = null;
    };

    /**
     * Captures the state change
     * @param {Boolean} visible the new state from store.
     */
    const handleStateChange = (newState) => {
        renderState(newState.valid ? views.form.valid : views.form.invalid, element);
    };

    /**
     * Updates the aria-describedby attribute on the fieldset for errors
     * @param {FormField} field
     */
    const handleAriaDescribedBy = (field) => {
        renderState(
            { attrs: { "aria-describedby": getElementId(field.errorText) } },
            field.element
        );
    };

    /**
     * Generate a new ID and set it for a given element.
     * @param {HTMLElement} element
     * @returns {String}
     */
    const setNewId = (element) => {
        const newId = `${componentName}-${
            element.name || element.tagName
        }-${randomNumGenerator()}`.toLowerCase();
        element.setAttribute("id", newId);
        return newId;
    };

    const getElementId = (element) => {
        const existingId = element.hasAttribute("id") && element.getAttribute("id");
        return existingId ? existingId : setNewId(element);
    };

    /**
     * When reporting an error message, combine the label and the error message.
     * @param {FormField} field
     * @returns
     */
    const generateErrorMessage = (field) => {
        const { label, errorText } = field;
        let message = "";
        if (label && label.content) {
            message = message.concat(`${label.content}: `);
        }
        if (errorText) {
            message = message.concat(errorText.innerHTML);
        }
        if (!message) {
            // Fallback to a default message
            message = message.concat(mergedProps.fallbackErrorMessage);
        }
        return message;
    };

    /**
     * When there is an error with a field, apply the right classes and let our store know about the error.
     * @param {FormField} field
     */
    const handleFieldError = (field) => {
        field.controls.forEach((control) => {
            // Only Fieldset, not individual radio buttons should receive
            if (field.controlType === "radio") return;

            renderState(views.control.invalid, control);
            // Handle injecting the error icon for specific control type.
            if (["inputText", "textarea"].includes(field.controlType)) {
                missingFieldIconInvalid(field, () => {
                    return injectErrorIcon(field);
                });
            }
            // Accessibility concerns for the component need to point to the errorText dynamically.
            if (field.errorText) {
                handleAriaDescribedBy(field);
            }
        });
        renderState(views.field.invalid, field.element);

        // Radio button attribute should receive fieldset
        if (field.controlType === "radio") {
            // Radio button only needs the fieldset to have the "aria" attributes and not individual radio buttons
            renderState(views.control.invalid, field.element);

            // Accessibility concerns for the component need to point to the errorText dynamically.
            if (field.errorText) {
                handleAriaDescribedBy(field);
            }
        }
        validityStore.dispatch(
            actions.setError({
                id: getElementId(field.element),
                // We want to set the controlId to focus if there is an error.
                // a11y guidelines point us to focus the first element.
                controlId: getElementId(field.controls[0]),
                message: generateErrorMessage(field),
            })
        );
    };

    /**
     * When our form field is valid, we want to make sure it's no longer in an error state.
     * @param {FormField} field
     */
    const handleFieldReset = (field) => {
        field.controls.forEach((control) => {
            renderState(views.control.valid, control);
        });
        if (field.controlType === "radio") {
            renderState(views.control.valid, field.element);
        } else {
            field.controls.forEach((control) => {
                renderState(views.control.valid, control);
            });
        }
        renderState(views.field.base, field.element);
        validityStore.dispatch(actions.removeError(getElementId(field.element)));
    };

    /**
     * When something happens to a form field, handle it differently based on it's validity.
     * @param {FormField} field
     * @param {Boolean} valid
     */
    const handleFieldValidation = (field, valid) => {
        if (valid) {
            handleFieldReset(field);
        } else {
            handleFieldError(field);
        }
    };

    /**
     * We want to loop through all our form fields and validate them depending on their type.
     * @param {[FormField]} formFields
     * @returns {Boolean} - whether or not the fields pass validation.
     */
    const validateFormFields = (formFields) => {
        for (const field of formFields) {
            if (["inputText", "textarea"].includes(field.controlType)) {
                handleFieldValidation(field, field.controls[0].checkValidity());
            } else if (field.controlType === "checkbox") {
                if (
                    isRequired(field.controls[0]) ||
                    field.element.getAttribute("aria-required") === "true"
                ) {
                    // Validates the checkbox if required
                    handleFieldValidation(field, field.controls[0].checked);
                }
            } else if (field.controlType === "checkboxGroup") {
                if (
                    isRequired(field.element) ||
                    field.element.getAttribute("aria-required") === "true"
                ) {
                    // Validates positively if at least 1 of the checkbox options has returned true
                    handleFieldValidation(
                        field,
                        field.controls.some((control) => {
                            return control.checked;
                        })
                    );
                }
            } else if (field.controlType === "radio") {
                if (isRequired(field.element)) {
                    // Only 1 radio button needs to be checked
                    handleFieldValidation(
                        field,
                        field.controls.some((control) => {
                            return control.checked;
                        })
                    );
                }
            } else if (["select", "datePicker"].includes(field.controlType)) {
                if (isRequired(field.controls[0])) {
                    handleFieldValidation(field, field.controls[0].checkValidity());
                }
            }
        }

        // Keep our forms validity up to date.
        const { errors } = validityStore.getState();
        const formIsValid = isEmptyObject(errors);
        validityStore.dispatch(actions.setValid(formIsValid));
        dispatchEvent(eventMap[formIsValid ? "success" : "failure"], element, errors);
    };

    /**
     * Checks whether or not we have a missing error icon.
     * @param {FormField} field
     * @param {Function} next
     * @returns
     */
    const missingFieldIconInvalid = (field, next) => {
        if (field.element.querySelector(`.${prefix}field__icon--invalid`)) {
            // Already exists.
            return;
        }
        next();
    };

    /**
     * Inject our error icon into the field.
     * @param {FormField} field
     * @returns
     */
    const injectErrorIcon = (field) => {
        const isWithButton = field.element.querySelector(
            `.${textInputClasses.wrapper}.${textInputClasses.withButton}`
        );

        // Input with button is a unique case where we inject the icon into the button.
        if (isWithButton) {
            const button = field.element.getElementsByTagName("BUTTON")[0];
            button.insertAdjacentHTML(
                "beforeend",
                alertNotice({
                    class: `${prefix}input-text__icon--end ${prefix}field__icon--invalid`,
                })
            );
            return;
        }

        field.controls[0].insertAdjacentHTML(
            "afterend",
            alertNotice({
                class: `${prefix}input-text__icon--end ${prefix}field__icon--invalid`,
            })
        );
    };

    /**
     * When we want to validate our form, we need to check all our form fields, so that we can
     * determine our validity.
     * @returns
     */
    const validate = () => {
        // Allow for our form fields to be dynamic up until the moment they hit submit.
        formFields = getFormFields(element);

        // Radio buttons are a special case (wrapped in a fieldset)
        formFields = formFields.concat(getRadioButtonGroups(element));
        const [checkboxes, groups] = getFieldsWithCheckbox(element);
        formFields = formFields.concat(checkboxes);
        formFields = formFields.concat(groups);

        if (formFields.length === 0) {
            return console.warn("form has no elements.");
        }

        // After our first submit - we use the validated view to tell our form fields that they can
        // show their error state if they have one.
        renderState(views.form.validated, element);

        validateFormFields(formFields);

        // Our Form has been validated at least one time.
        hasRunValidation = true;
    };

    /**
     * Captures the click and dispatch the action to change the current state.
     */
    const handleSubmit = (event) => {
        validate();

        // After we have executed our validation - get our valid check
        const { valid } = validityStore.getState();

        // Prevent submitting the form if we have errors
        if (!valid) {
            event && event.preventDefault();
        }
    };

    /**
     * When handling radio buttons, they are unlike other fields because it is wrapped inside the
     * fieldset. The fieldset will determine the required attribute. We will also use the children
     * of that fieldset to determine the children because "name" isn't always reliable.
     * @param {HTMLElement} element
     * @returns {FormField[]}
     */
    const getRadioButtonGroups = (element) => {
        const radioButtonGroups = [];
        const fieldsets = Array.from(element.getElementsByTagName("FIELDSET"));

        if (!fieldsets) {
            return radioButtonGroups;
        }

        fieldsets.forEach((fieldset) => {
            const radioButtons = Array.from(fieldset.children).filter((child) => {
                return child.classList.contains(`${prefix}radio-button`);
            });

            if (radioButtons.length === 0) {
                return;
            }

            radioButtonGroups.push(FormField(fieldset, { type: "radio" }));
        });

        return radioButtonGroups;
    };

    /**
     * When handling checkboxes, they are unlike other fields because it is wrapped inside the
     * fieldset. The fieldset will determine the required attribute. It will return 2 arrays of the checkbox groups and standalone checkboxes
     * @param {HTMLElement} element
     * @returns {FormField[]}
     */
    const getFieldsWithCheckbox = (element) => {
        const checkboxGroups = Array.from(element.querySelectorAll(`.${checkboxClasses.group}`));
        const checkboxList = Array.from(element.querySelectorAll(`.${checkboxClasses.base}`));
        const groups = [];
        const checkboxes = [];

        if (!checkboxGroups && !checkboxList) {
            return;
        }

        checkboxGroups.forEach((checkboxGroup) => {
            const _checkboxes = Array.from(checkboxGroup.children).filter((child) => {
                return child.classList.contains(checkboxClasses.base);
            });

            _checkboxes.forEach((child) => {
                if (checkboxList.includes(child)) {
                    checkboxList.splice(checkboxList.indexOf(child), 1);
                }
            });

            groups.push(FormField(checkboxGroup, { type: "checkboxGroup" }));
        });

        checkboxList.forEach((checkbox) => {
            if (!checkbox) return;
            checkboxes.push(FormField(checkbox, { type: "checkbox" }));
        });

        return [checkboxes, groups];
    };

    const getErrors = () => {
        return validityStore.getState().errors;
    };

    const isValid = () => {
        if (!hasRunValidation) {
            console.warn(
                `The form has not been validated yet, will default to "true". Please check the validity first by calling the API validate() or on "submit" event.`
            );
        }
        return validityStore.getState().valid;
    };

    // Init

    // Make sure that our form is in it's proper state.
    renderState(views.form.basic, element);

    // We want our form's state to be maintained by a store + custom reducer.
    const validityStore = createStore(formReducer, { valid: true, errors: {} });

    validityStore.subscribe(handleStateChange);

    element.addEventListener("submit", handleSubmit);

    const api = {
        element,
        dispose,
        getErrors,
        isValid,
        validate,
    };

    element[componentName] = api;

    return api;
};

export default Form;
